import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { KeyboardScrollView } from "@/components/ui/keyboard-view";

import { spotApi, venuesApi, type VenueCandidate } from "@/api";
import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { useAuthStore } from "@/state/auth-store";
import i18n from "@/i18n";

export default function ClaimSpot() {
  useTranslation();
  const c = useTheme();
  const router = useRouter();
  const isAuthed = useAuthStore((s) => s.status === "authenticated");

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<VenueCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function search() {
    const q = query.trim();
    if (q.length < 2) return;
    setSearching(true);
    setError(null);
    try {
      setResults(await venuesApi.search(q));
    } catch {
      setError(i18n.t("business.claim.searchError"));
    } finally {
      setSearching(false);
    }
  }

  async function claim(candidate: VenueCandidate) {
    setClaimingId(candidate.placeId);
    setError(null);
    try {
      const spot = await spotApi.claim(candidate);
      router.replace(`/business/spots/${spot.id}`);
    } catch {
      setError(i18n.t("business.claim.claimError"));
      setClaimingId(null);
    }
  }

  if (!isAuthed) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <View style={styles.centered}>
          <ThemedText variant="subtitle" center>
            {i18n.t("business.claim.signInTitle")}
          </ThemedText>
          <ThemedText variant="body" tone="secondary" center>
            {i18n.t("business.claim.signInBody")}
          </ThemedText>
          <Pressable
            onPress={() => router.replace("/(auth)/welcome")}
            style={[styles.primaryBtn, { backgroundColor: c.tint }]}
          >
            <ThemedText variant="bodyStrong" tone="onTint">
              {i18n.t("business.claim.signIn")}
            </ThemedText>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <Pressable onPress={() => router.back()} hitSlop={10}>
          <Ionicons name="chevron-back" size={26} color={c.text} />
        </Pressable>
        <ThemedText variant="bodyStrong">
          {i18n.t("business.claim.title")}
        </ThemedText>
        <View style={{ width: 26 }} />
      </View>

      <KeyboardScrollView contentContainerStyle={styles.content}>
        <ThemedText variant="body" tone="secondary">
          {i18n.t("business.claim.intro")}
        </ThemedText>

        <View style={[styles.searchRow, { borderColor: c.border }]}>
          <Ionicons name="search" size={18} color={c.textMuted} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            onSubmitEditing={search}
            returnKeyType="search"
            placeholder={i18n.t("business.claim.placeholder")}
            placeholderTextColor={c.textMuted}
            style={[styles.input, { color: c.text }]}
          />
          <Pressable
            onPress={search}
            style={[styles.searchBtn, { backgroundColor: c.tint }]}
          >
            <ThemedText variant="caption" tone="onTint">
              {i18n.t("business.claim.search")}
            </ThemedText>
          </Pressable>
        </View>

        {error ? (
          <ThemedText variant="caption" tone="danger">
            {error}
          </ThemedText>
        ) : null}

        {searching ? (
          <ActivityIndicator color={c.tint} style={styles.loader} />
        ) : (
          results.map((venue) => (
            <View
              key={venue.placeId}
              style={[
                styles.result,
                { backgroundColor: c.surface, borderColor: c.border },
              ]}
            >
              <View style={styles.resultCopy}>
                <ThemedText variant="bodyStrong" numberOfLines={1}>
                  {venue.name}
                </ThemedText>
                {venue.address ? (
                  <ThemedText
                    variant="caption"
                    tone="secondary"
                    numberOfLines={1}
                  >
                    {venue.address}
                  </ThemedText>
                ) : null}
              </View>
              <Pressable
                onPress={() => claim(venue)}
                disabled={claimingId !== null}
                style={[styles.claimBtn, { backgroundColor: c.tint }]}
              >
                {claimingId === venue.placeId ? (
                  <ActivityIndicator size="small" color={c.onTint} />
                ) : (
                  <ThemedText variant="caption" tone="onTint">
                    {i18n.t("business.claim.claim")}
                  </ThemedText>
                )}
              </Pressable>
            </View>
          ))
        )}
      </KeyboardScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    minHeight: 56,
    paddingHorizontal: Spacing.lg,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  content: {
    width: "100%",
    maxWidth: 640,
    alignSelf: "center",
    padding: Spacing.xl,
    gap: Spacing.md,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.md,
    padding: Spacing.xl,
  },
  searchRow: {
    minHeight: 50,
    paddingHorizontal: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  input: { flex: 1, fontSize: 16, paddingVertical: Spacing.sm },
  searchBtn: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.md,
  },
  loader: { marginVertical: Spacing.lg },
  result: {
    minHeight: 62,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  resultCopy: { flex: 1, gap: 2 },
  claimBtn: {
    minWidth: 68,
    minHeight: 36,
    paddingHorizontal: Spacing.md,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.md,
  },
  primaryBtn: {
    minHeight: 50,
    paddingHorizontal: Spacing.xl,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
  },
});
